import { FC, Dispatch, SetStateAction, useEffect, useState } from 'react'
import StepBullet from 'src/components/basic/StepBullet'
import StandardInput from 'src/components/basic/StandardInput'
import StandardFormatInput from 'src/components/basic/StandardFormatInput'
import FirmPracticeType from 'src/components/application/formComponents/FirmPracticeType'
import FirmContactInfo from 'src/components/application/formComponents/FirmContactInfo'
import AddressFormCustom from 'src/components/application/formComponents/AddressFormCustom'
import FirmSecondaryContact from 'src/components/application/formComponents/FirmSecondaryContact'
import {
  FormContainer,
  FormItemContainer,
} from 'src/components/application/defaultComponents'
import { FirmSectionData } from 'src/dtos/applicationData'
import { validateDateField } from 'src/utils/formDataManipulation'
import { ValidationErrorMessage } from 'src/components/basic/ValidationErrorMessage/ValidationErrorMessage'

interface Props {
  firmData: FirmSectionData;
  setFirmData: Dispatch<SetStateAction<FirmSectionData>>;
  validateSteps: { [key: string]: boolean; };
  validatedFields: string[]
  renewalMode?: boolean;
}

const MainForm: FC<Props> = ({ firmData, setFirmData, validateSteps, renewalMode, validatedFields }) => {
  const [willScroll, setWillScroll] = useState(0)

  useEffect(() => {
    if(validatedFields.length > 0 && validatedFields.length > willScroll) {
      document.getElementById(validatedFields[0])?.scrollIntoView({ behavior: 'smooth' })
    }
    setWillScroll(validatedFields.length)
  }, [validatedFields, willScroll])

  const mailingAddressErrorCodes = ['EC4', 'EC5', 'EC6', 'EC7', 'EC8']
  const billingAddressErrorCodes = ['EC18', 'EC19', 'EC20', 'EC21', 'EC22']

  return (
    <FormContainer maxWidth='624px'>
      <FormItemContainer>
        <StepBullet id="step1" isComplete={validateSteps.step1} >1</StepBullet>
        <StandardInput
          label="Firm name"
          name="firmName"
          labelSize='high'
          value={firmData.firmName ?? ''}
          onChange={e => setFirmData(prev => ({ ...prev, firmName: e.target.value }))}
          isIncorrect={validatedFields.includes('step1')}
          errorMessage={validatedFields.includes('step1') ? 'This is a required field' : undefined}
          errorCode = 'EC1'
        />
      </FormItemContainer>
      <FormItemContainer>
        <StepBullet id="step2"  isComplete={validateSteps.step2} >2</StepBullet>
        <FirmPracticeType
          setPracticeType={value => setFirmData(prev => ({ ...prev, practiceType: value }))}
          practiceType={firmData?.practiceType}
          isIncorrect={validatedFields.includes('step2')}
          errorCode = 'EC10'
        />
      </FormItemContainer>
      <div style={{ paddingLeft: '32px' }}>
        <ValidationErrorMessage errorCode='EC10' />
      </div>
      {!renewalMode ? (
        <FormItemContainer>
          <StepBullet id="step3"  isComplete={validateSteps.step3} >3</StepBullet>
          <StandardFormatInput
            label="When was your firm established or when was the firm registered with the Secretary of State (if applicable)?"
            labelSize='high'
            maskStyle='date'
            leftIcon='calendar'
            value={firmData.establishmentDate ?? ''}
            onValueChange={({ formattedValue }) => setFirmData(prev => ({ ...prev, establishmentDate: formattedValue }))}
            isIncorrect={validatedFields.includes('step3')}
            errorMessage={validatedFields.includes('step3') ? validateDateField(firmData.establishmentDate) : ''}
            errorCode='EC184'
          />
        </FormItemContainer>
      ) : null}
      <FormItemContainer>
        <StepBullet id="step4"  isComplete={validateSteps.step4} >{renewalMode ? 3 : 4}</StepBullet>
        <FirmContactInfo isIncorrect={validatedFields.includes('step4')} isWebsiteUrlCorrect={validatedFields.includes('step10')} setData={setFirmData} contactData={firmData?.contactInfo} />
      </FormItemContainer>
      <div style={{ paddingLeft: '32px' }}>
        <ValidationErrorMessage errorCode='EC181' />
      </div>
      <FormItemContainer maxWidth='660px'>
        <StepBullet id="step5"  isComplete={validateSteps.step5} >{renewalMode ? 4 : 5}</StepBullet>
        <AddressFormCustom
          title='Principal mailing address for your firm'
          setAddressData={addressData => setFirmData(prev => ({ ...prev, mailAddress: { ...prev.mailAddress, ...addressData } }))}
          addressData={firmData?.mailAddress}
          errorCodes={mailingAddressErrorCodes}
          isIncorrect={validatedFields.includes('step5')}
        />
      </FormItemContainer>
      <div style={{ paddingLeft: '32px' }}>
        <ValidationErrorMessage errorCode='EC182' />
      </div>
      <FormItemContainer>
        <StepBullet id="step9"  isComplete={validateSteps.step9} >{renewalMode ? 5 : 6}</StepBullet>
        <StandardInput
          label="Number of offices"
          type="number"
          labelSize='high'
          value={firmData.numberOfOffices ?? 0}
          onChange={e => setFirmData(prev => ({ ...prev, numberOfOffices: Number(e.target.value) }))}
          isIncorrect={validatedFields.includes('step9') && !firmData?.numberOfOffices}
          errorMessage={validatedFields.includes('step9') && !firmData?.numberOfOffices ? 'This is a required field' : ''}
          errorCode = 'EC9'
        />
      </FormItemContainer>
      <FormItemContainer maxWidth='660px'>
        <StepBullet id="step6"  isComplete={validateSteps.step6} >{renewalMode ? 6 : 7}</StepBullet>
        <AddressFormCustom
          title='Do you have a billing address different than your principal mailing address?'
          showOptions={true}
          setAddressData={addressData => setFirmData(prev => ({ ...prev, billingAddress: { ...prev.billingAddress, ...addressData } }))}
          addressData={firmData?.billingAddress}
          hasAddress={firmData?.isBillingEqualToMail !== undefined ? !firmData.isBillingEqualToMail : undefined}
          setHasAddress={hasAddress => setFirmData(prev => ({
            ...prev,
            isBillingEqualToMail: !hasAddress,
            billingAddress: hasAddress ? prev.billingAddress : undefined
          }))}
          isIncorrect={validatedFields.includes('step6')}
          errorCodes={billingAddressErrorCodes}
        />
      </FormItemContainer>
      <div style={{ paddingLeft: '32px' }}>
        <ValidationErrorMessage errorCode='EC4'/>
      </div>
      <div style={{ paddingLeft: '32px' }}>
        <ValidationErrorMessage errorCode='EC183'/>
      </div>
      <div style={{ paddingLeft: '32px' }}>
        <ValidationErrorMessage errorCode='EC307'/>
      </div>
      
      <FormItemContainer maxWidth='660px'>
        <StepBullet id="step7"  isComplete={validateSteps.step7} >{renewalMode ? 7 : 8}</StepBullet>
        <FirmSecondaryContact
          setContactData={contactData => setFirmData(prev => ({ ...prev, secondaryContact: { ...prev.secondaryContact, ...contactData } }))}
          hasContact={'isSecondaryContactAdded' in firmData ? firmData.isSecondaryContactAdded : undefined}
          setHasSecondaryContact={hasContact => setFirmData(prev => ({ ...prev, isSecondaryContactAdded: hasContact }))}
          contactData={firmData?.secondaryContact}
          isIncorrect={validatedFields.includes('step7')}
        />
      </FormItemContainer>
    </FormContainer>
  )
}

export default MainForm